import type { Analysis } from '@teacher-shared/analysis';
import { TeachingPage, ClassSwitcher, useClass, Card, StudentLink, WithData, useAnalysis, AssessmentSwitcher, ProvisionalBanner, breadcrumbOf, CodeTag } from '@/components/teaching/ui';
import { KpiRow, KpiCard } from '@/components/teaching/kit';

export function Misconceptions() {
  return <WithData state={useAnalysis()} title="Common mistakes">{({ a, A }) => <MisconceptionsView a={a} A={A} />}</WithData>;
}

function MisconceptionsView({ a, A }: { a: string; A: Analysis }) {
  const [cls, setCls] = useClass(A.classes);
  const rows = A.misconceptionRows(cls);
  const max = Math.max(1, ...rows.map((r) => r.students.length));

  return (
    <TeachingPage title="Common mistakes" breadcrumb={breadcrumbOf(A)} banner={<ProvisionalBanner A={A} />} actions={<><AssessmentSwitcher current={a} /><ClassSwitcher value={cls} onChange={setCls} classes={A.classes} /></>}>
      <KpiRow cols={3}>
        <KpiCard value={rows.length} label="Different mistakes seen" />
        <KpiCard value={rows[0] ? rows[0].students.length : '-'} label="Most common" hint={rows[0] ? `${rows[0].meaning} (${rows[0].code})` : undefined} />
        <KpiCard value={rows.reduce((n, r) => n + r.students.length, 0)} label="Times seen in writing" />
      </KpiRow>
      <div class="text-[12.5px] text-[var(--color-text-muted)] mb-4">
        Ranked by how many students showed each one in writing. Blank answers and missing papers never count as a mistake.
      </div>
      <div class="flex flex-col gap-3">
        {rows.length === 0 && <div class="text-[12.5px] text-[var(--color-text-muted)]">No mistakes recorded yet for this test. When you tag a mistake while marking, it shows up here.</div>}
        {rows.map((row) => {
          const items = Array.from(new Set(row.students.map((x) => x.itemId)));
          const example = row.students.find((x) => x.evidence)?.evidence;
          return (
            <Card key={row.code}>
              <div class="flex items-start gap-3">
                                <div class="flex-1 min-w-0">
                  <div class="text-[14px] font-semibold text-[var(--color-text)]">{row.meaning}<CodeTag code={row.code} /></div>
                  <div class="text-[11.5px] text-[var(--color-text-faint)] mt-0.5">
                    Seen in {items.sort((a, b) => A.items.findIndex((i) => i.id === a) - A.items.findIndex((i) => i.id === b)).join(', ')}
                  </div>
                  <div class="mt-2 h-2 rounded bg-[var(--color-elevated)] overflow-hidden">
                    <div class="h-full rounded" style={{ width: `${(row.students.length / max) * 100}%`, background: '#f3b4b4' }} />
                  </div>
                  {example && <div class="mt-2 text-[12px] italic text-[var(--color-text-muted)]">e.g. {example}</div>}
                  <div class="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[12.5px]">
                    {row.students.map((x) => (
                      <span key={x.s.id}>
                        <StudentLink a={a} id={x.s.id} name={x.s.name} cls={cls} />
                        {cls === 'all' && <span class="text-[10px] text-[var(--color-text-faint)] ml-0.5">{x.s.cls}</span>}
                      </span>
                    ))}
                  </div>
                </div>
                <div class="text-[22px] font-semibold tabular-nums text-[var(--color-text)]">{row.students.length}</div>
              </div>
            </Card>
          );
        })}
      </div>
    </TeachingPage>
  );
}
